<template>
  <div>
    <vx-card ref="MainCard" class="pb-3" title="IOT Integrity Report - Registered Accounts">
      <p class="mb-4 text-grey">
        Only accounts registered here (Enable checked) can generate an IOT Integrity report on the portal
        or be picked up by the daily "other accounts" batch. StoreID 0 means the whole account; a non-zero
        StoreID scopes the registration to a single store.
      </p>
      <div>
        <vs-table
          ref="gridTable"
          v-model="selected"
          pagination
          :max-items="itemsPerPage"
          search
          :data="rows"
          class="vs-con-loading__container"
        >
          <div slot="header" class="flex flex-wrap-reverse items-center flex-grow">
            <vs-button class="rounded-lg mr-2" color="warning" type="filled" @click="loadRows()" icon-pack="feather" icon="icon-refresh-ccw">Reload</vs-button>
            <vs-button class="rounded-lg" color="primary" type="filled" @click="openCreate()" icon-pack="feather" icon="icon-plus">Add Account</vs-button>
          </div>
          <template slot="thead">
            <vs-th sort-key="accountName">Account</vs-th>
            <vs-th sort-key="accountID">Account ID</vs-th>
            <vs-th sort-key="storeID">Store ID</vs-th>
            <vs-th sort-key="enable">Enabled</vs-th>
            <vs-th sort-key="notionPageId">Notion Page</vs-th>
            <vs-th>Action</vs-th>
          </template>
          <template slot-scope="{data}">
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td :data="tr.accountName">{{ tr.accountName }}</vs-td>
              <vs-td :data="tr.accountID">{{ tr.accountID }}</vs-td>
              <vs-td :data="tr.storeID">{{ tr.storeID }}</vs-td>
              <vs-td :data="tr.enable">
                <vs-chip :color="tr.enable ? 'success' : 'grey'">{{ tr.enable ? 'Enabled' : 'Disabled' }}</vs-chip>
              </vs-td>
              <vs-td :data="tr.notionPageId">
                <vs-chip v-if="tr.notionPageId" color="success">Linked</vs-chip>
                <span v-else class="text-grey">Not linked</span>
              </vs-td>
              <vs-td id="iconContainer" class="whitespace-no-wrap">
                <vx-tooltip class="flex" text="Edit">
                  <feather-icon icon="EditIcon" svgClasses="w-5 h-5 hover:text-success stroke-current" @click="openEdit(tr)" />
                </vx-tooltip>
                <vx-tooltip class="flex" text="Remove">
                  <feather-icon icon="TrashIcon" svgClasses="w-5 h-5 hover:text-danger stroke-current" @click="removeRow(tr)" />
                </vx-tooltip>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>

      <vs-popup :title="editingId ? 'Edit Registration' : 'Add Account'" :active.sync="popupActive">
        <div class="vx-col md:w-full w-full mt-5" v-if="!editingId">
          <label class="w-full">Select Account</label>
          <v-select class="w-full" placeholder="Search / Select Account" :options="accountDataList" v-model="accountDropdown" name="Account" />
        </div>
        <div class="vx-col md:w-full w-full mt-5" v-else>
          <label class="w-full">Account</label>
          <vs-input class="w-full" :value="formData.accountName" disabled />
        </div>
        <div class="vx-col md:w-full w-full mt-5">
          <label class="w-full">Store</label>
          <v-select
            class="w-full"
            placeholder="Select an account first"
            :options="storeDataList"
            :disabled="!currentAccountId"
            v-model="storeDropdown"
          />
        </div>
        <div class="vx-col md:w-full w-full mt-5">
          <vs-checkbox v-model="formData.enable">Enable</vs-checkbox>
        </div>
        <div class="vx-col md:w-full w-full mt-5">
          <label class="w-full">Notion Page ID</label>
          <vs-input class="w-full" placeholder="Parent page ID - a 'Data Integrity Reports' database is auto-created here (leave blank to disable Notion publishing)" v-model="formData.notionPageId" />
          <p class="text-grey mt-1" style="font-size: 0.8rem;">
            One-time setup per client: (1) share the Notion integration with this page (Share &rarr; invite integration),
            then (2) after the first successful publish, open the auto-created "Data Integrity Reports" database
            (it appears inline on this page) and switch its default view to Calendar, then (3) in that view's
            "..." menu set "Open pages in" to Full page - Notion's API cannot set any of these three for you.
          </p>
        </div>
        <div class="vx-row flex flex-wrap items-center mt-5">
          <vs-spacer />
          <div class="vx-col">
            <vs-button class="rounded-lg mr-3" color="success" type="border" @click="save()">Save</vs-button>
            <vs-button class="rounded-lg mr-3" color="warning" type="border" @click="closePopup()">Cancel</vs-button>
          </div>
        </div>
      </vs-popup>
    </vx-card>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "IOTIntegrityAccountsAdmin",

  components: { vSelect },

  data: function () {
    return {
      itemsPerPage: 10,
      selected: [],
      rows: [],
      accountDataList: [],
      accountDropdown: null,
      storeDataList: [],
      storeDropdown: null,
      popupActive: false,
      editingId: null,
      formData: {
        accountName: "",
        enable: true,
        notionPageId: "",
      },
    };
  },

  computed: {
    // The account currently in scope for the popup - whichever dropdown is
    // relevant depending on create vs. edit mode. The Store dropdown stays
    // disabled until this resolves to a real account.
    currentAccountId: function () {
      if (this.editingId) return this.formData.accountID;
      return this.accountDropdown ? this.accountDropdown.value : null;
    },
  },

  watch: {
    accountDropdown: function (val) {
      this.storeDropdown = null;
      this.storeDataList = [];
      if (val && val.value) {
        this.loadStoreDropdown(val.value);
      }
    },
  },

  mounted: function () {
    this.loadRows();
    this.loadAccountDropdown();
  },

  methods: {
    loadRows: function () {
      var self = this;
      var onSuccess = function (response) { self.rows = response.data || []; };
      self.$ajaxGet(self, "IOTIntegrityAccounts", onSuccess, function () {});
    },

    loadAccountDropdown: function () {
      var self = this;
      var onSuccess = function (response) {
        self.accountDataList = (response.data || []).map(function (item) {
          return { label: item.name, value: item.id };
        });
      };
      self.$ajaxGet(self, "Accounts/GetAccountdropdown", onSuccess, function () {});
    },

    // Whole Account (StoreID 0) is always offered first, ahead of this
    // account's real stores - not a Store table row, so it's added client-side.
    loadStoreDropdown: function (accountId, preselectStoreId) {
      var self = this;
      var onSuccess = function (response) {
        var stores = (response.data || []).map(function (item) {
          return { label: item.name, value: item.id };
        });
        self.storeDataList = [{ label: "Whole Account (StoreID 0)", value: 0 }].concat(stores);
        if (preselectStoreId != null) {
          self.storeDropdown = self.storeDataList.filter(function (s) { return s.value === preselectStoreId; })[0] || null;
        }
      };
      self.$ajaxGet(self, "IOTIntegrityAccounts/GetStoresForAccount?accountId=" + encodeURIComponent(accountId), onSuccess, function () {});
    },

    openCreate: function () {
      this.editingId = null;
      this.accountDropdown = null;
      this.storeDropdown = null;
      this.storeDataList = [];
      this.formData = { accountName: "", enable: true, notionPageId: "" };
      this.popupActive = true;
    },

    openEdit: function (tr) {
      this.editingId = tr.id;
      this.formData = { accountName: tr.accountName, accountID: tr.accountID, enable: tr.enable, notionPageId: tr.notionPageId || "" };
      this.storeDropdown = null;
      this.loadStoreDropdown(tr.accountID, tr.storeID);
      this.popupActive = true;
    },

    closePopup: function () {
      this.popupActive = false;
    },

    save: function () {
      var self = this;
      if (!this.editingId && (!this.accountDropdown || !this.accountDropdown.value)) {
        self.$vs.notify({ title: "Select an account", text: "Choose an account before saving.", color: "warning" });
        return;
      }
      if (!this.storeDropdown) {
        self.$vs.notify({ title: "Select a store", text: "Choose a store (or Whole Account) before saving.", color: "warning" });
        return;
      }

      var payload = {
        accountID: this.editingId ? this.formData.accountID : this.accountDropdown.value,
        storeID: this.storeDropdown.value,
        enable: !!this.formData.enable,
        notionPageId: this.formData.notionPageId || null,
      };

      var onSuccess = function () {
        self.closePopup();
        self.loadRows();
        self.$vs.notify({ title: "Saved", text: "IOT Integrity account registration saved.", color: "success" });
      };
      var onFinally = function () {};

      if (this.editingId) {
        self.$ajaxPut(self, "IOTIntegrityAccounts/" + this.editingId, payload, onSuccess, onFinally);
      } else {
        self.$ajaxPost(self, "IOTIntegrityAccounts", payload, onSuccess, onFinally);
      }
    },

    removeRow: function (tr) {
      var self = this;
      var onSuccess = function () {
        self.loadRows();
        self.$vs.notify({ title: "Removed", text: "IOT Integrity account registration removed.", color: "success" });
      };
      self.$ajaxDelete(self, "IOTIntegrityAccounts/" + tr.id, onSuccess, function () {});
    },
  },
};
</script>

<style>
#iconContainer span {
  display: flex;
  justify-content: space-evenly;
}

#iconContainer span .con-vs-tooltip {
  padding: 0px 2.5px;
}
</style>
